/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  AssetMediaType,
  AssetOption,
  AssetOptionType,
  Ducks,
  EntryPoint,
  Folder,
  Percentage,
  ProductPrice,
  WatchOption,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, select } from 'redux-saga/effects';
import * as Context from '../context';
import getWatchOptions from '../options/watch';
import getWishlistOptions from '../options/wishlist';
import getPurchaseOptions from '../options/purchases';
import getRecordingOptions from '../options/recordings';
import getTrailerOptions from '../options/trailer';
import { SeasonsData } from '../../../../core/store/ducks/pi/seasons';
import getFolderPurchasesOptions from '../options/folderPurchases';
import * as Data from '../data';

// Purchase options and watch options
function* getEntitlementRelatedOptions(
  ctx: Context.PiContext,
  asset: Asset,
  clones: Asset[]
): SagaIterator<AssetOption[]> {
  try {
    const vodClonesPrices: ProductPrice[] = yield select(Data.Selectors.vodPrices, ctx);

    const watchOptions: WatchOption[] = yield call(
      getWatchOptions,
      ctx.entryAssetMediaType,
      asset,
      clones,
      vodClonesPrices
    );
    if (watchOptions.length > 0) {
      return watchOptions;
    }

    // when no wath options available then get the purchase options
    const vat: Percentage | undefined = yield select(Ducks.Household.Selectors.data.vat);
    if (vat !== undefined) {
      // when vat is not supported the value is 0
      return yield call(getPurchaseOptions, clones, vodClonesPrices, vat);
    }
    // if vat is undefined then we shall not calculate options
    return [];
  } catch {
    return [];
  }
}

export function* getLiveWatchOptions(asset: Asset): SagaIterator<AssetOption[]> {
  return yield call(getWatchOptions, AssetMediaType.LINEAR, asset, [asset], []);
}

export function* getOptions(
  ctx: Context.PiContext,
  asset: Asset,
  clones: Asset[],
  folders: Folder[],
  seasons?: SeasonsData
): SagaIterator<AssetOption[]> {
  let optionsResults: AssetOption[][] = yield all([
    call(getEntitlementRelatedOptions, ctx, asset, clones),
    call(getFolderPurchasesOptions, folders),
    call(getRecordingOptions, ctx.entryPoint, asset, clones, seasons),
    call(getTrailerOptions, clones),
    call(getWishlistOptions, asset, ctx.entryPoint),
  ]);

  // handle trailers position
  const trailerOptions = optionsResults[3];
  const entitlementRelatedOptions = optionsResults[0];

  // if there are no watch options but there are trailers then move the trailers to the top, so user has something to watch
  if (entitlementRelatedOptions[0]?.type !== AssetOptionType.WATCH && trailerOptions.length) {
    optionsResults = [trailerOptions, optionsResults[0], optionsResults[1], optionsResults[2], optionsResults[4]];
  }

  const options: AssetOption[] = optionsResults.reduce((results, curOptions) => [...results, ...curOptions]);

  return options;
}

export function* getBarOptions(entryAssetMediaType: AssetMediaType, asset: Asset): SagaIterator<AssetOption[]> {
  const [watchOptions, recordingOptions]: AssetOption[][] = yield all([
    call(getWatchOptions, entryAssetMediaType, asset, [asset], []),
    call(getRecordingOptions, EntryPoint.TV_GUIDE, asset, [asset]),
  ]);

  const options: AssetOption[] = [];
  if (watchOptions && watchOptions.length) {
    options.push(watchOptions[0]);
  }
  if (recordingOptions && recordingOptions.length) {
    options.push(recordingOptions[0]);
  }
  return options;
}
